import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MIcon } from '../../lib/micon';
import { LdSheet } from '../../components/ui/LdSheet';
import { useIsMobile } from '../../lib/hooks';
import { useKlassen } from '../../contexts/KlassenContext';
import { useSchueler } from '../../contexts/SchuelerContext';
import { aufzaehlung } from '../schueler/profil/format';
import type { KlasseFachEintrag } from '../../types';
import './klasse.css';

// ── Welle K1: EIN Kopf für alle Klassen-Seiten ─────────────────────────────────
// Titel „Klasse 7b“ mit Pfeil (Klassenwahl: Handy LdSheet, PC Menü), darunter eine Zeile
// Kontext („Klassenleitung, 26 Kinder, bei dir Deutsch und Mathe“), seltene Aktionen im ⋯-Menü.
// Reihenfolge der Klassen wie die bisherigen Chips (eindeutigeKlassen: Klassenleitung vorn).

export type KopfMenuEintrag = { label: string; icon: string; onClick: () => void };

export function KlassenKopf({ klassen, aktiveKlasse, onWechsel, menu = [], kompakt = false, bereich }: {
  klassen: KlasseFachEintrag[];                 // eindeutig, sortiert
  aktiveKlasse: KlasseFachEintrag | null;
  onWechsel: (k: KlasseFachEintrag) => void;
  menu?: KopfMenuEintrag[];
  kompakt?: boolean;                            // eigenständige Seiten (Fehlzeiten, Klassenbuch): kleinerer Abstand unten
  bereich?: string;                             // eigenständige Seiten: Bereich vorn in der Kontextzeile („Fehlzeiten, …“)
}) {
  const isMobile = useIsMobile();
  const { meineKlassen } = useKlassen();
  const { schuelerListe } = useSchueler();
  const [wahlOffen, setWahlOffen] = useState(false);
  const pfeil = useRef<HTMLButtonElement>(null);

  if (!aktiveKlasse) return null;
  const kid = Number(aktiveKlasse.klasse_id);
  const eintraege = meineKlassen.filter(k => Number(k.klasse_id) === kid);
  const leitung = eintraege.some(k => k.rolle === 'klassenleiter');
  const faecher = Array.from(new Set(eintraege.map(k => (k.fach ?? '').trim()).filter(Boolean)));
  const kinder = schuelerListe.filter(s => Number(s.klasse_id) === kid).length;
  const kontext = [
    bereich ?? null,
    leitung ? 'Klassenleitung' : null,
    `${kinder} ${kinder === 1 ? 'Kind' : 'Kinder'}`,
    faecher.length ? `bei dir ${aufzaehlung(faecher)}` : null,
  ].filter(Boolean).join(', ');
  const mehrere = klassen.length > 1;

  return (
    <header className={`ld-kk${kompakt ? ' ld-kk--kompakt' : ''}`}>
      <div className="ld-kk__zeile">
        <div className="ld-kk__titel-block">
          <div className="ld-kk__titel-zeile">
            <h1 className="ld-kk__titel">Klasse {aktiveKlasse.klasse_name}</h1>
            {mehrere && (
              <button ref={pfeil} type="button" className="ld-kk__wahl" aria-label="Klasse wechseln" aria-haspopup={isMobile ? 'dialog' : 'menu'}
                aria-expanded={wahlOffen} onClick={() => setWahlOffen(o => !o)}>
                <MIcon name="expand_more" size={22} />
              </button>
            )}
          </div>
          <p className="ld-kk__kontext">{kontext}</p>
        </div>
        {menu.length > 0 && <KkMenu eintraege={menu} />}
      </div>

      {mehrere && isMobile && (
        <LdSheet open={wahlOffen} onClose={() => setWahlOffen(false)} kopf={{ titel: 'Klasse wählen' }} maxWidth={480}>
          <div className="ld-kk-wahl">
            {klassen.map(k => <KlassenWahlZeile key={k.klasse_id} k={k} aktiv={Number(k.klasse_id) === kid} meineKlassen={meineKlassen}
              onClick={() => { setWahlOffen(false); onWechsel(k); }} />)}
          </div>
        </LdSheet>
      )}
      {mehrere && !isMobile && wahlOffen && (
        <PortalMenu anker={pfeil.current} links onClose={() => setWahlOffen(false)} label="Klasse wählen">
          {klassen.map(k => <KlassenWahlZeile key={k.klasse_id} k={k} aktiv={Number(k.klasse_id) === kid} meineKlassen={meineKlassen}
            onClick={() => { setWahlOffen(false); onWechsel(k); }} />)}
        </PortalMenu>
      )}
    </header>
  );
}

function KlassenWahlZeile({ k, aktiv, meineKlassen, onClick }: { k: KlasseFachEintrag; aktiv: boolean; meineKlassen: KlasseFachEintrag[]; onClick: () => void }) {
  const eintraege = meineKlassen.filter(x => Number(x.klasse_id) === Number(k.klasse_id));
  const leitung = eintraege.some(x => x.rolle === 'klassenleiter');
  const faecher = Array.from(new Set(eintraege.map(x => (x.fach ?? '').trim()).filter(Boolean)));
  return (
    <button type="button" role="menuitemradio" aria-checked={aktiv} className={`ld-kk-wahl__item${aktiv ? ' ld-kk-wahl__item--aktiv' : ''}`} onClick={onClick}>
      <span className="ld-kk-wahl__name">Klasse {k.klasse_name}</span>
      <span className="ld-kk-wahl__info">{[leitung ? 'Klassenleitung' : null, faecher.join(', ') || null].filter(Boolean).join(', ')}</span>
      {aktiv && <MIcon name="check" size={18} className="ld-kk-wahl__haken" />}
    </button>
  );
}

// ⋯-Menü (seltene Aktionen)
export function KkMenu({ eintraege }: { eintraege: KopfMenuEintrag[] }) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={knopf} type="button" className="ld-kk__mehr" aria-label="Weitere Aktionen" aria-haspopup="menu" aria-expanded={offen} onClick={() => setOffen(o => !o)}>
        <MIcon name="more_horiz" size={20} />
      </button>
      {offen && (
        <PortalMenu anker={knopf.current} onClose={() => setOffen(false)} label="Weitere Aktionen">
          {eintraege.map(e => (
            <button key={e.label} type="button" role="menuitem" className="ld-kk-menu__item" onClick={() => { setOffen(false); e.onClick(); }}>
              <MIcon name={e.icon} size={18} />{e.label}
            </button>
          ))}
        </PortalMenu>
      )}
    </>
  );
}

// Menü als Portal (Overlays nicht im Kartenfluss), am Auslöser ausgerichtet
export function PortalMenu({ anker, onClose, label, links = false, rolle = 'menu', children }: {
  anker: HTMLElement | null; onClose: () => void; label: string; links?: boolean; rolle?: 'menu' | 'listbox' | 'dialog'; children: React.ReactNode;
}) {
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Welle K2: in einem offenen Fenster (LdSheet = Radix-Dialog) ins Fenster selbst hängen — außerhalb wären
  // Klicks gesperrt und das Fenster ginge zu. Dort absolut zum Fenster, sonst fest zum Bildschirm.
  const ziel = anker?.closest('[role="dialog"]') as HTMLElement | null;
  useLayoutEffect(() => {
    if (!anker) return;
    const r = anker.getBoundingClientRect();
    const c = ziel ? ziel.getBoundingClientRect() : { top: 0, left: 0, right: window.innerWidth };
    setPos(links ? { top: r.bottom - c.top + 6, left: Math.max(8, r.left - c.left - 8) } : { top: r.bottom - c.top + 6, right: Math.max(8, c.right - r.right) });
  }, [anker, links, ziel]);
  // Welle K4: reicht der Platz unten nicht (Fenster schneidet ab), nach oben aufklappen
  useLayoutEffect(() => {
    const m = menuRef.current;
    if (!m || !anker || !pos) return;
    const r = anker.getBoundingClientRect();
    const unten = ziel ? ziel.getBoundingClientRect().bottom : window.innerHeight;
    const oben = ziel ? ziel.getBoundingClientRect().top : 0;
    const h = m.offsetHeight;
    if (r.bottom + 6 + h > unten - 8 && r.top - 6 - h >= oben + 8) {
      const neu = r.top - oben - 6 - h;
      if (Math.abs(neu - pos.top) > 1) setPos(p => (p ? { ...p, top: neu } : p));
    }
    // seitlich im Fenster bzw. Bildschirm halten
    const breite = ziel ? ziel.clientWidth : window.innerWidth;
    const w = m.offsetWidth;
    if (pos.left != null && pos.left + w > breite - 8) { const l = Math.max(8, breite - 8 - w); if (Math.abs(l - pos.left) > 1) setPos(p => (p ? { ...p, left: l } : p)); }
    if (pos.right != null && breite - pos.right - w < 8) { const rr = Math.max(8, breite - 8 - w); if (Math.abs(rr - pos.right) > 1) setPos(p => (p ? { ...p, right: rr } : p)); }
  }, [pos, anker, ziel]);
  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [onClose]);
  if (!pos) return null;
  const imFenster = ziel ? { position: 'absolute' as const } : undefined;
  return createPortal(
    <>
      <div className="ld-kk-menu__hinter" style={imFenster} onClick={onClose} />
      <div ref={menuRef} className="ld-kk-menu" role={rolle} aria-label={label} style={{ ...pos, ...imFenster }}>{children}</div>
    </>, ziel ?? document.body);
}

// ── Reiter ─────────────────────────────────────────────────────────────────────
export type KlassenUnter = 'uebersicht' | 'noten' | 'fehlzeiten' | 'klassenbuch' | 'notizen' | 'elternbeirat' | 'aktionen' | 'ausgaben';
export type KlassenBereich = 'schueler' | 'unterricht' | 'klasse';

export const BEREICH_VON: Record<KlassenUnter, KlassenBereich> = {
  uebersicht: 'schueler', noten: 'schueler', fehlzeiten: 'schueler',
  klassenbuch: 'unterricht', notizen: 'unterricht',
  elternbeirat: 'klasse', aktionen: 'klasse', ausgaben: 'klasse',
};
const BEREICHE: { key: KlassenBereich; label: string; icon: string; unter: { key: KlassenUnter; label: string }[] }[] = [
  { key: 'schueler',   label: 'Schüler',    icon: 'group',     unter: [{ key: 'uebersicht', label: 'Übersicht' }, { key: 'noten', label: 'Noten' }, { key: 'fehlzeiten', label: 'Fehlzeiten' }] },
  { key: 'unterricht', label: 'Unterricht', icon: 'menu_book', unter: [{ key: 'klassenbuch', label: 'Klassenbuch' }, { key: 'notizen', label: 'Notizen' }] },
  { key: 'klasse',     label: 'Klasse',     icon: 'school',    unter: [{ key: 'elternbeirat', label: 'Elternbeiräte' }, { key: 'aktionen', label: 'Aktionen' }, { key: 'ausgaben', label: 'Ausgaben' }] },
];

// Handy: Segment-Leiste (volle Breite) + Unterreiter als Text mit Unterstrich — höchstens zwei Leisten,
// keine zum Wischen. PC: eine Reiterzeile, unter „Klasse“ eine kleine zweite Leiste.
export function KlassenReiter({ unter, onUnter }: { unter: KlassenUnter; onUnter: (u: KlassenUnter) => void }) {
  const isMobile = useIsMobile();
  const bereich = BEREICH_VON[unter];
  const letzte = useRef<Record<KlassenBereich, KlassenUnter>>({ schueler: 'uebersicht', unterricht: 'klassenbuch', klasse: 'elternbeirat' });
  letzte.current[bereich] = unter;
  const akt = BEREICHE.find(b => b.key === bereich)!;

  if (isMobile) return (
    <nav className="ld-kr" aria-label="Bereiche der Klasse">
      <div className="ld-kr-seg" role="tablist">
        {BEREICHE.map(b => (
          <button key={b.key} type="button" role="tab" aria-selected={b.key === bereich}
            className={`ld-kr-seg__item${b.key === bereich ? ' ld-kr-seg__item--aktiv' : ''}`}
            onClick={() => onUnter(letzte.current[b.key])}>
            <MIcon name={b.icon} size={18} />{b.label}
          </button>
        ))}
      </div>
      <Unterreiter eintraege={akt.unter} unter={unter} onUnter={onUnter} />
    </nav>
  );

  const pcReiter: { key: KlassenUnter | 'klasse'; label: string }[] = [
    ...BEREICHE[0].unter, ...BEREICHE[1].unter, { key: 'klasse', label: 'Klasse' },
  ];
  return (
    <nav className="ld-kr ld-kr--pc" aria-label="Bereiche der Klasse">
      <div className="ld-kr-linie" role="tablist">
        {pcReiter.map(r => {
          const aktiv = r.key === 'klasse' ? bereich === 'klasse' : r.key === unter;
          return (
            <button key={r.key} type="button" role="tab" aria-selected={aktiv} className={`ld-kr-linie__item${aktiv ? ' ld-kr-linie__item--aktiv' : ''}`}
              onClick={() => onUnter(r.key === 'klasse' ? letzte.current.klasse : r.key)}>{r.label}</button>
          );
        })}
      </div>
      {bereich === 'klasse' && (
        <div className="ld-kr-klein" role="tablist" aria-label="Klasse">
          {BEREICHE[2].unter.map(u => (
            <button key={u.key} type="button" role="tab" aria-selected={u.key === unter}
              className={`ld-kr-klein__item${u.key === unter ? ' ld-kr-klein__item--aktiv' : ''}`} onClick={() => onUnter(u.key)}>{u.label}</button>
          ))}
        </div>
      )}
    </nav>
  );
}

function Unterreiter({ eintraege, unter, onUnter }: { eintraege: { key: KlassenUnter; label: string }[]; unter: KlassenUnter; onUnter: (u: KlassenUnter) => void }) {
  return (
    <div className="ld-kr-linie ld-kr-linie--handy" role="tablist">
      {eintraege.map(u => (
        <button key={u.key} type="button" role="tab" aria-selected={u.key === unter}
          className={`ld-kr-linie__item${u.key === unter ? ' ld-kr-linie__item--aktiv' : ''}`} onClick={() => onUnter(u.key)}>{u.label}</button>
      ))}
    </div>
  );
}
